/**
 * =============================================================================
 * Página: Permissões (central unificada) — agora em formato de MODAL
 * Release v2.1.0 — 2026-09-07
 *
 * Centraliza todas as páginas de configuração de permissões:
 *   • Perfis de Permissão         (matriz profile × menu × ações)
 *   • Permissões de Dashboard     (visibilidade granular por dashboard)
 *   • Permissões por Fase Tarefas (matriz profile × fase × ações)
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-17 v2.3.0 - A seção "Permissões por Fase de Negócios" passa
 *            a exigir o módulo CRM habilitado no tenant (mesmo critério já
 *            aplicado à seção de Fases de Tarefas).
 *   RELEASE: 2026-09-08 v2.2.0 - Nova seção "Permissões por Fase de Negócios",
 *            centralizando a matriz perfil × fase configurada por workspace CRM.
 *   RELEASE: 2026-09-07 v2.1.0 - A troca de seção passa a substituir a entrada
 *            atual do histórico, evitando exigir vários cliques para fechar o
 *            modal depois de navegar pelo menu lateral.
 *   RELEASE: 2026-09-07 v2.0.0 - A central deixa de ser uma página com abas e
 *            passa a abrir como modal grande (PermissionsCenterDialog), com as
 *            seções em menu lateral e seta de voltar no cabeçalho claro.
 *            Regras de visibilidade, deep-link ?tab= e fallback preservados.
 *   RELEASE: 2026-06-02 v1.0.0 - Versão inicial (página com abas).
 *
 * Regras críticas (inalteradas):
 *   1. A rota é protegida por requireAdmin no <PermissionGuard>.
 *   2. Cada seção possui sua própria chave de permissão; sem acesso, a seção
 *      simplesmente não aparece.
 *   3. Seção ativa controlada por ?tab=... na URL (deep-link).
 *   4. Deep-link para seção sem permissão cai na primeira seção disponível.
 * =============================================================================
 */

import { useEffect, useMemo, Suspense, lazy } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Shield, LayoutDashboard, ListChecks, Users, Kanban, Wrench, HeartHandshake, Scale } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { useTasksModuleSettings } from '@/hooks/useTasksModuleSettings';
import {
  PermissionsCenterDialog,
  type PermissionsSection,
} from '@/components/tenant/permissions/PermissionsCenterDialog';

// Carregamento sob demanda das páginas existentes (zero refactor — usadas como
// conteúdo de cada seção). Lazy mantém o bundle inicial leve.
// 2026-10-09: loaders nomeados para permitir pré-carregamento (abertura rápida
// das seções de fase, que têm pacote maior que Perfis).
const loadProfilesSection = () => import('@/pages/tenant/PermissionProfiles');
const loadDashboardsSection = () => import('@/pages/crm/DashboardPermissions');
const loadTaskPhasesSection = () => import('@/pages/tasks/TaskPhasePermissions');
const loadDealPhasesSection = () => import('@/components/crm/workspace/WorkspacePhasePermissionsSection');
const PermissionProfiles = lazy(loadProfilesSection);
const DashboardPermissions = lazy(loadDashboardsSection);
const TaskPhasePermissions = lazy(loadTaskPhasesSection);
// 2026-10-22: Tarefas, Retenção e Jurídico têm seções próprias (mesma tela, tipo fixo)
const NormalTaskPhasePermissions = lazy(() =>
  loadTaskPhasesSection().then((m) => ({ default: () => <m.default fixedType="normal" /> })),
);
const RetentionPhasePermissions = lazy(() =>
  loadTaskPhasesSection().then((m) => ({ default: () => <m.default fixedType="retention" /> })),
);
const LegalPhasePermissions = lazy(() =>
  loadTaskPhasesSection().then((m) => ({ default: () => <m.default fixedType="legal" /> })),
);
// Seção própria da OS: reutiliza a MESMA tela de fases em modo service_orders (sem duplicar)
const ServiceOrderPhasePermissions = lazy(() =>
  loadTaskPhasesSection().then((m) => ({
    default: () => <m.default mode="service_orders" />,
  })),
);
// Seção "Permissões por Fase de Negócios" (matriz perfil × fase por workspace CRM)
const WorkspacePhasePermissionsSection = lazy(loadDealPhasesSection);

// Catálogo declarativo de seções. Adicionar nova seção aqui propaga
// automaticamente para a filtragem por permissão e para o fallback.
interface PermissionsTabDef {
  id: 'profiles' | 'dashboards' | 'task-phases' | 'retention-phases' | 'legal-phases' | 'os-phases' | 'deal-phases';
  label: string;
  icon: typeof Shield;
  /** Componente já existente, renderizado como conteúdo da seção. */
  Component: React.LazyExoticComponent<React.ComponentType>;
  /** Condição de visibilidade da seção (avaliada em runtime). */
  isVisible: (ctx: PermissionsTabVisibilityCtx) => boolean;
}

interface PermissionsTabVisibilityCtx {
  isAdmin: boolean;
  canView: (menuKey: string) => boolean;
  /** Flag do tenant — necessária para a seção "Fases de Tarefas". */
  tasksEnabled: boolean;
  /** Flag do tenant — necessária para a seção "Fases de Negócios" (CRM). */
  crmEnabled: boolean;
  /** 2026-10-22: flags dos submódulos Retenção e Jurídico. */
  retentionEnabled: boolean;
  legalEnabled: boolean;
}

const PERMISSIONS_TABS: PermissionsTabDef[] = [
  {
    id: 'profiles',
    label: 'Perfis de Permissão',
    icon: Users,
    Component: PermissionProfiles,
    // Perfis sempre visíveis para admin do tenant (controla as próprias matrizes).
    isVisible: ({ isAdmin }) => isAdmin,
  },
  {
    id: 'dashboards',
    label: 'Permissões de Dashboard',
    icon: LayoutDashboard,
    Component: DashboardPermissions,
    // Mantém a key histórica "settings" (mesma usada na rota antiga).
    isVisible: ({ isAdmin, canView }) => isAdmin && canView('settings'),
  },
  {
    id: 'task-phases',
    label: 'Permissões por Fase de Tarefas',
    icon: ListChecks,
    Component: NormalTaskPhasePermissions,
    // Só faz sentido se o módulo Tarefas estiver habilitado no tenant.
    isVisible: ({ isAdmin, canView, tasksEnabled }) =>
      isAdmin && tasksEnabled && canView('tasks_phases'),
  },
  {
    // 2026-10-22: menu principal próprio (antes era subaba de Tarefas)
    id: 'retention-phases',
    label: 'Permissões por Fase de Retenção',
    icon: HeartHandshake,
    Component: RetentionPhasePermissions,
    isVisible: ({ isAdmin, canView, retentionEnabled }) =>
      isAdmin && retentionEnabled && canView('retention_phases'),
  },
  {
    id: 'legal-phases',
    label: 'Permissões por Fase de Jurídico',
    icon: Scale,
    Component: LegalPhasePermissions,
    isVisible: ({ isAdmin, canView, legalEnabled }) =>
      isAdmin && legalEnabled && canView('legal_phases'),
  },
  {
    // 2026-10-09: seção própria da Ordem de Serviço (mesma tela, modo OS).
    // canView('os_phases') já exige o módulo OS habilitado no tenant.
    id: 'os-phases',
    label: 'Permissões por Fase de O.S',
    icon: Wrench,
    Component: ServiceOrderPhasePermissions,
    isVisible: ({ isAdmin, canView }) => isAdmin && canView('os_phases'),
  },
  {
    id: 'deal-phases',
    label: 'Permissões por Fase de Negócios',
    icon: Kanban,
    Component: WorkspacePhasePermissionsSection,
    // Mesma chave de menu usada na página de workspaces do CRM.
    // v2.3.0: só faz sentido quando o módulo CRM está habilitado no tenant.
    isVisible: ({ isAdmin, canView, crmEnabled }) =>
      isAdmin && crmEnabled && canView('crm_workspaces'),
  },
];

export default function Permissions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin, canView, loading } = usePermissions();
  const { currentTenant } = useAuth();
  const { retentionEnabled, legalEnabled } = useTasksModuleSettings();

  // Resolve dinamicamente as seções visíveis ao usuário atual.
  const visibleTabs = useMemo(() => {
    if (loading) return [];
    const tasksEnabled =
      (currentTenant as unknown as Record<string, unknown>)?.tasks_enabled === true;
    // v2.3.0: flag do módulo CRM (gate da seção "Fases de Negócios")
    const crmEnabled = currentTenant?.crm_enabled === true;
    return PERMISSIONS_TABS.filter((tab) =>
      tab.isVisible({ isAdmin, canView, tasksEnabled, crmEnabled, retentionEnabled, legalEnabled })
    );
  }, [loading, isAdmin, canView, currentTenant, retentionEnabled, legalEnabled]);

  // 2026-10-22: links antigos (?tab=task-phases&type=retention|legal) abrem o novo menu
  const rawTab = searchParams.get('tab');
  const rawType = searchParams.get('type');
  const requestedTab = (rawTab === 'task-phases' && (rawType === 'retention' || rawType === 'legal')
    ? `${rawType}-phases`
    : rawTab) as PermissionsTabDef['id'] | null;

  // Calcula a seção ativa: respeita ?tab=... se for visível; caso contrário,
  // cai na primeira seção disponível (fallback de segurança contra deep-link
  // para seções sem permissão).
  const activeTab = useMemo(() => {
    if (visibleTabs.length === 0) return null;
    const isRequestedVisible = visibleTabs.some((t) => t.id === requestedTab);
    return isRequestedVisible ? requestedTab! : visibleTabs[0].id;
  }, [visibleTabs, requestedTab]);

  // Sincroniza a URL quando o fallback acontece — mantém o link copiável correto.
  useEffect(() => {
    if (!activeTab) return;
    if (searchParams.get('tab') !== activeTab) {
      // Preserva os demais parâmetros (ex.: ?type=retention)
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Pré-carrega o código das seções ao abrir a central (troca instantânea)
  useEffect(() => {
    const t = setTimeout(() => {
      void loadProfilesSection(); void loadTaskPhasesSection();
      void loadDashboardsSection(); void loadDealPhasesSection();
    }, 0);
    return () => clearTimeout(t);
  }, []);

  /** Fechar o modal volta para a tela anterior (ou para o painel). */
  const handleClose = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/tenant/dashboard');
  };

  // Loading enquanto resolvemos permissões.
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Defesa em profundidade: se o admin não tem permissão de nenhuma seção,
  // mostramos um estado vazio em vez de uma área em branco.
  if (visibleTabs.length === 0 || !activeTab) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <Shield className="mb-3 h-10 w-10 text-muted-foreground/60" strokeWidth={1.5} />
        <p className="text-sm font-medium text-foreground">
          Nenhuma configuração de permissão disponível
        </p>
        <p className="mt-1 max-w-sm text-xs text-muted-foreground">
          Você não tem acesso a nenhuma das páginas de permissões neste tenant.
        </p>
      </div>
    );
  }

  const sections: PermissionsSection[] = visibleTabs.map((t) => ({
    id: t.id,
    label: t.label,
    icon: t.icon,
  }));

  const ActiveComponent =
    visibleTabs.find((t) => t.id === activeTab)?.Component ?? visibleTabs[0].Component;

  return (
    <PermissionsCenterDialog
      open
      onOpenChange={(aberto) => {
        if (!aberto) handleClose();
      }}
      sections={sections}
      activeSection={activeTab}
      // A seção muda sem empilhar histórico; assim o modal fecha em uma ação.
      onSectionChange={(id) => setSearchParams({ tab: id }, { replace: true })}
    >
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        }
      >
        <ActiveComponent />
      </Suspense>
    </PermissionsCenterDialog>
  );
}
